import React, { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import toast from 'react-hot-toast';

const REPORT_TEMPLATES = [
  { id: 'standard', name: 'Standard Report', description: 'Traditional report layout with header and footer' },
  { id: 'detailed', name: 'Detailed Report', description: 'Comprehensive format with extra details' },
  { id: 'compact', name: 'Compact Report', description: 'Space-efficient layout' },
  { id: 'modern', name: 'Modern Report', description: 'Contemporary design with colors' },
  { id: 'minimal', name: 'Minimal Report', description: 'Clean and simple layout' },
];

export default function ReportTemplateSettings() {
  const { settings, loading, updateSettings } = useSettings();
  const [selectedTemplate, setSelectedTemplate] = useState('standard');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setSelectedTemplate(settings.report_template || 'standard');
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);

    const result = await updateSettings({
      report_template: selectedTemplate,
    });

    if (result.success) {
      toast.success('Report template updated successfully');
    } else {
      toast.error(result.error || 'Failed to update template');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Report Template</h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose the template that will be used for all lab and radiology reports.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REPORT_TEMPLATES.map((template) => (
            <div
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedTemplate === template.id
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {selectedTemplate === template.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              <div className="aspect-[3/4] bg-white border border-gray-200 rounded mb-3 flex items-center justify-center">
                <div className="text-center p-4">
                  <div className="text-xs text-gray-400 mb-2">Preview</div>
                  <div className="text-2xl font-bold text-gray-300">{template.name}</div>
                </div>
              </div>

              <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
              <p className="text-sm text-gray-600">{template.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || selectedTemplate === settings.report_template}
          className="flex items-center space-x-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Template'}</span>
        </button>
      </div>
    </div>
  );
}
